import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { getStudentMonthlySummary } from "../../api/attendanceReportApi";
import "./Attendance.css";

const periodApi = createLookupApi("periods");
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/**
 * Direct port of digicampusbd's `student-attendance-report-monthly-section-summary`
 * ("Attendance Monthly Summary") — per-student attendance-taken/present/
 * absent/leave totals for one class+period+month+year. Confirmed via
 * `monthlyStudentAttendanceSummary`'s own native SQL (a plain 3-table
 * GROUP BY/COUNT, no holiday logic) — new `GET
 * /api/attendance/reports/student/monthly-summary` mirrors it verbatim.
 */
export default function StudentMonthlySummaryPage() {
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [monthName, setMonthName] = useState(MONTHS[new Date().getMonth()]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    periodApi.list().then((list) => {
      setPeriods(list);
      if (list.length > 0) setPeriodId(String(list[0].id));
    });
  }, []);

  const handleSearch = async () => {
    setError("");
    setLoading(true);
    try {
      setRows(await getStudentMonthlySummary(classConfigId, periodId, monthName, year));
    } catch (err) {
      setError("Failed to load attendance summary.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Attendance Monthly Summary</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="attendance-filters">
        <label>
          Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Period
          <select value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Month
          <select value={monthName} onChange={(e) => setMonthName(e.target.value)}>
            {MONTHS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label>
          Year
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        rows && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Student Name</th>
                  <th>Roll</th>
                  <th>Mobile</th>
                  <th>Attendance Taken</th>
                  <th>Present</th>
                  <th>Absent</th>
                  <th>Leave</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.identificationId}>
                    <td>{r.customStudentId}</td>
                    <td>{r.studentName}</td>
                    <td>{r.studentRoll}</td>
                    <td>{r.mobileNo}</td>
                    <td>{r.attendanceTaken}</td>
                    <td>{r.totalPresent}</td>
                    <td>{r.totalAbsent}</td>
                    <td>{r.totalLeave}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8}>No attendance recorded for this selection.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )
      )}
    </MainLayout>
  );
}
